import { useContext } from "react";
import { useIntl } from "react-intl";

import { EventListRequest, EventRead, ProjectRead } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { objectFromEntries, removeNullUndefined } from "@nens/shared-utils";

import {
  JOB_SEARCH_KEY,
  PROCESS_SEARCH_KEY,
  PUBLICATION_SEARCH_KEY,
  PUBLICATION_SELECTED_COMMENT_KEY,
  PUBLICATION_SHOW_MAP_KEY,
} from "@/config/query-parameter-consts";
import { getProjectTabQueryParam } from "@/features/project";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useEventListQuery } from "@/queries/query-events";
import { useProjectsQueries } from "@/queries/query-project";
import { ProjectContext } from "@/stores/project-provider";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";
import { disabledFileHistoryProps } from "@/utils/disabled-file-history-props";

export type UseEventsListProps = {
  filterBy: "user" | "project";
  initialPageSize?: number;
};

export function useEventsList({ filterBy, initialPageSize = 10 }: UseEventsListProps) {
  const intl = useIntl();
  const authInfo = useContext(TenantContext);
  const userId = !authInfo.isPublic ? authInfo.user.id : undefined;
  const { id: projectId } = useContext(ProjectContext);
  const { page, offset, limit, setPageSize, setPage } = useRanaTableData({
    keyAddon: "projectEvents",
    initialPageSize,
  });
  const [showOnlyMine, setShowOnlyMine] = useRanaSearchState("evMine", false);

  const getEventsParams: Omit<EventListRequest, "tenantId"> = { limit, offset };

  if (filterBy === "user" || showOnlyMine) {
    getEventsParams.userId = userId;
  }
  if (filterBy === "project") {
    getEventsParams.projectId = projectId;
  }

  const eventsQuery = useEventListQuery(getEventsParams);
  const { isLoading } = eventsQuery;

  const events = eventsQuery.data?.items ?? [];

  // Only get all the needed projects linked to events
  const eventProjectIds = removeNullUndefined([
    ...new Set(events.map(({ projectId }) => projectId)),
  ]);
  const projects = useProjectsQueries(eventProjectIds.map((projectId) => ({ id: projectId }))).map(
    (project) => project.data
  );
  const projectsIdToProjectMap = objectFromEntries(
    removeNullUndefined(projects).map((project) => [project.id, project])
  );
  const deletedPublications: { [key: string]: true } = {};

  const getRowAndLinkProps = (event: EventRead) => {
    const { message, entityType, projectId, data, entityId } = event;

    // Get correct tab
    const tab = (() => {
      switch (entityType) {
        case "process":
        case "job":
          return "processes";
        case "project_file":
        case "project_directory":
          return "files";
        case "user":
        case "invitation":
          return "users";
        case "publication":
        case "publication_comment":
          return "publications";
        default:
          return "overview";
      }
    })();

    const projectTabParam = getProjectTabQueryParam(tab);

    const otherQueryParams = (() => {
      if (tab === "files")
        return `&${convertFileIdToQueryParams(
          entityId + (entityType === "project_directory" ? "/" : ""),
          (data as { ref: string })?.ref
        )}`;

      if (tab === "publications") {
        if (entityType === "publication_comment") {
          return `&${PUBLICATION_SEARCH_KEY}=${
            (data as { publication_id: string }).publication_id
          }&${PUBLICATION_SELECTED_COMMENT_KEY}=${entityId}&${PUBLICATION_SHOW_MAP_KEY}=true`;
        } else {
          return `&${PUBLICATION_SEARCH_KEY}=${entityId}`;
        }
      }

      if (tab === "processes") {
        if (entityType === "process") return `&${PROCESS_SEARCH_KEY}=${entityId}`;
        if (entityType === "job") return `&${JOB_SEARCH_KEY}=${entityId}`;
      }

      return "";
    })();

    // See if publication has ever been deleted
    if (tab === "publications" && message.includes(" deleted."))
      deletedPublications[entityId] = true;
    const publicationDeletedText = deletedPublications[entityId]
      ? intl.formatMessage({
          id: "message.publication_deleted",
          defaultMessage: "Publication doesn't exist anymore",
        })
      : undefined;

    // See if project has ever been deleted
    const project: ProjectRead | undefined = projectsIdToProjectMap[projectId + ""];
    const slug = project?.slug;
    const projectDoesntExistText = !slug
      ? intl.formatMessage({
          id: "message.project_deleted",
          defaultMessage: "Project doesn't exist anymore",
        })
      : undefined;

    // See if this file version is deleting
    const { title: fileDeletedText } = disabledFileHistoryProps(intl, message);

    const disabledText = publicationDeletedText || projectDoesntExistText || fileDeletedText;

    return {
      projectName: project?.name,
      disabledText,
      to: `/${authInfo.tenant}/projects/${slug}`,
      queryParams: `${projectTabParam}${otherQueryParams}`,
    };
  };

  const paginationProps = {
    page,
    numPages: Math.ceil((eventsQuery.data?.total ?? 0) / limit),
    limit,
    setPageSize,
    setPage,
  };

  const filterProps = {
    checked: showOnlyMine,
    setChecked: (checked: boolean) => {
      setPage(1);
      setShowOnlyMine(checked);
    },
  };

  return {
    events,
    getRowAndLinkProps,
    isLoading,
    paginationProps,
    filterProps,
  };
}
